import { noop } from '@tanstack/react-query';
import * as React from 'react';

import {
  type GeoLocation,
  geocodeLocation,
} from '@/components/audit/widgets/company-gps-coordinates/utils';

// "Am Weidendamm 1a, 10117 Berlin"
export const DEFAULT_LOCATION: GeoLocation = {
  latitude: 52.521855,
  longitude: 13.388933,
};

export const useGoogleMap = <TElement extends HTMLElement>(
  containerRef: React.RefObject<TElement | null>,
  onNewLocation: (location: GeoLocation) => void,
  getInitialLocation: () => Promise<GeoLocation> = () => Promise.resolve(DEFAULT_LOCATION),
) => {
  const mapInstanceRef = React.useRef<google.maps.Map | null>(null);

  React.useEffect(() => {
    (async () => {
      if (containerRef.current && !mapInstanceRef.current) {
        const defaultLocation = await getInitialLocation();

        const map = new window.google.maps.Map(containerRef.current, {
          center: {
            lat: defaultLocation.latitude,
            lng: defaultLocation.longitude,
          },
          zoom: 16,
          disableDefaultUI: true,
          zoomControl: true,
        });
        mapInstanceRef.current = map;

        map.addListener('click', async (mapsMouseEvent: google.maps.MapMouseEvent) => {
          const latitude = mapsMouseEvent.latLng?.lat();
          const longitude = mapsMouseEvent.latLng?.lng();

          if (latitude && longitude) {
            const geocoderResults = await geocodeLocation({
              location: { lat: latitude, lng: longitude },
            }).catch(noop);

            const address = geocoderResults?.[0]?.formatted_address;

            onNewLocation({ latitude, longitude, address });
          }
        });

        onNewLocation(defaultLocation);
      }
    })();

    return () => {
      if (mapInstanceRef.current) {
        google.maps.event.clearInstanceListeners(mapInstanceRef.current);
        mapInstanceRef.current = null;
      }
    };
  }, [containerRef, getInitialLocation, onNewLocation]);

  return mapInstanceRef.current;
};
